import styled from '@emotion/styled';
import React, {useRef} from 'react';

import {ChevronSmallDown} from '@ELib/assets/images';
import {mixinTruncate} from '@ELib/styles/mixins';
import {classy, classyArray} from '@ELib/utilities/HelperFunctions';
import {ConditionalWrapper} from '@ELib/utilities/jsx';

import {useMountEffect} from '../../utilities/hooks/effectHelpers';
import {ETooltip} from '../ETooltip';

import {StyledButton, StyledSmallButton} from './ButtonElements';
import {useButtonMenu} from './EButtons.hooks';
import {ActionItem} from './EButtons.types';

export type BaseButtonProps = {
  children: React.ReactNode;
  /** Optional class name; required for nesting styling in other components. */
  className?: string;
  /** Optional class name to style the button wrapper element `<span>` */
  wrapperClassName?: string;
  /** Button icon to be shown on the left side of the children. */
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /** Button icon to be shown on the right side of the children. */
  EndIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /** Flag whether this button is disabled. */
  disabled?: boolean;
  /** Click handler (ignored if `extraActions` is passed). */
  onClick?: (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** Extra actions to be shown in the select box. If this is an empty array, the button will be disabled. */
  extraActions?: ActionItem[];
  /** Flag whether this button has full width or only fitted to content. */
  fullWidth?: boolean;
  /** Button title that will be shown as a tooltip. */
  title?: string;
  /** Button type such as submit */
  type?: 'button' | 'submit' | 'reset';
  /** button styling based on button function */
  variant?: 'danger' | 'flat' | 'outline';
  /** Thinner style to match with NEIconButton */
  newStyle?: boolean;
  /** allowes the button text to break line if the button is to small */
  breakLine?: boolean;
  /** string used as a testId for e2e tests */
  testId?: string;
  /** tell the component to render the extraActions menu inline */
  disableMenuPortal?: boolean;
  /** allows to get the focus on this button automatically */
  autoFocus?: boolean;
};

export type ButtonProps = Omit<
  BaseButtonProps,
  'flat' | 'danger' | 'flatBordered'
>;

export const BaseButton = ({
  onClick,
  children,
  className = '',
  wrapperClassName = '',
  title,
  extraActions,
  Icon,
  EndIcon,
  fullWidth,
  disabled,
  ButtonElement,
  type,
  variant,
  newStyle,
  breakLine,
  testId,
  disableMenuPortal,
  autoFocus,
  ...props
}: BaseButtonProps & {
  ButtonElement: typeof StyledButton | typeof StyledSmallButton;
}): React.ReactElement => {
  const ref = useRef<HTMLButtonElement>(null);

  useMountEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  });

  const {menu, handleClick} = useButtonMenu({
    extraActions,
    onClick,
    disablePortal: disableMenuPortal,
  });
  return (
    <ConditionalWrapper
      condition={Boolean(title)}
      wrapper={(wrapperChildren) => (
        <ETooltip title={title!}>{wrapperChildren}</ETooltip>
      )}
    >
      <span className={classyArray(['EButtonWrapper', wrapperClassName])}>
        <ButtonElement
          ref={ref}
          className={classyArray([
            variant,
            className,
            classy({'new-style': Boolean(newStyle)}),
          ])}
          data-testid={testId ? `EButton-${testId}` : undefined}
          disabled={disabled}
          fullWidth={fullWidth}
          type={type}
          onClick={handleClick}
          {...props}
        >
          {Icon ? <Icon /> : null}
          <TextContainer className={breakLine ? 'breakLine' : undefined}>
            <span>{children}</span>
          </TextContainer>
          {extraActions ? <ChevronSmallDown /> : null}
          {EndIcon ? <EndIcon /> : null}
        </ButtonElement>
        {menu}
      </span>
    </ConditionalWrapper>
  );
};

const TextContainer = styled.span`
  display: flex;
  height: var(--icon-dimension);
  align-items: center;

  // truncate long text
  min-width: 0;
  span {
    ${mixinTruncate}
  }

  &.breakLine {
    height: unset;
    min-height: var(--icon-dimension);
    align-items: center;

    span {
      white-space: pre-wrap;
    }
  }
`;
